import React, { useState } from 'react'
import { Panel } from 'primereact/panel'
import { Divider } from 'primereact/divider'
import { Button } from 'primereact/button'

import { ChargePoint, Connector } from '../../redux/api/web-api'
import useText from '../../hooks/useText'
import Text from '../../../ui/Text'
import { isChargingConnector } from '../../../lib/findChargingConnector'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'

export type ChargePointOverviewWidgetProps = {
    chargePoint: ChargePoint
}

export function ChargePointOverviewWidget({ chargePoint }: ChargePointOverviewWidgetProps): React.ReactElement {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    const getConnectorInfo = (chargePoint: ChargePoint, connector: Connector) => {
        const connectorInfo = {
            name: adaptConnectorNaming(connector.name),
            factoryCurrent: connector.factoryCurrent,
            supplyCurrent: chargePoint.supplyCurrent,
            userCurrent: connector.userCurrent || 0,
            installationPhase: connector.installationPhase,
        }

        return <Text code="msgOcppConnectorInfo" values={{ ...connectorInfo }} />
    }
    const [learnMoreText, setLearnMoreText] = useState(false)
    return (
        <Panel
            toggleable
            header={
                <div className="flex">
                    <span className="mr-1 mt-2">{text('lblChargerOverview')}</span>
                    <Button
                        className="ml-2"
                        text
                        severity="info"
                        onClick={() => setLearnMoreText(!learnMoreText)}
                        style={{ padding: '6px' }}
                    >
                        {learnMoreText ? text('btnShowLess') : text('btnShowMore')}
                    </Button>
                </div>
            }
            data-testid="charge-point-overview-widget"
        >
            {learnMoreText && (
                <>
                    {chargePoint.connectors
                        ?.filter(isChargingConnector)
                        ?.map((connector) => (
                            <div className="text-700 mr-2 mb-2 ml-2">{getConnectorInfo(chargePoint, connector)}</div>
                        ))}
                    <Divider />
                </>
            )}

            <div className="ml-3 mr-4">
                <div className="flex justify-content-between mb-3">
                    <span>
                        <Text code="lblChargingEfficiencyService"></Text>
                    </span>
                    {chargePoint?.ocppEnabled ? (
                        <i className="pi pi-check text-green-300 font-bold"></i>
                    ) : (
                        <i className="pi pi-times text-gray-500 font-bold" title={text('lblDisabledStatus')}></i>
                    )}
                </div>
                <div className="flex justify-content-between mb-3">
                    <span>
                        <Text code="lbLoadbalancerEnabled"></Text>
                    </span>
                    {chargePoint?.loadBalancerGroupId && chargePoint.inActiveLoadBalancerGroup ? (
                        <i
                            className="pi pi-check text-green-300 font-bold"
                            title={text('lblChargerIsLoadbalanced')}
                        ></i>
                    ) : (
                        <i
                            className="pi pi-times text-gray-500 font-bold"
                            title={text('lblChargerIsNotLoadbalanced')}
                        ></i>
                    )}
                </div>
                <div className="flex justify-content-between">
                    <span>
                        <Text code="lblWeeklyScheduleIsSet"></Text>
                    </span>
                    <span className="flex flex-column">
                        {chargePoint.connectors?.filter(isChargingConnector)?.map((connector) => {
                            const getConnectorScheduleIcon = () => {
                                if (connector?.hasSchedule === null) {
                                    return (
                                        <i
                                            className="pi pi-question text-gray-600 font-bold"
                                            title={text('lblUnknown')}
                                        />
                                    )
                                }
                                if (connector?.hasSchedule) {
                                    return (
                                        <i
                                            className="pi pi-check text-green-300 font-bold"
                                            title={text('lblChargerHasSchedules', { connectorName: connector?.name })}
                                        />
                                    )
                                }
                                return (
                                    <i
                                        className="pi pi-times text-gray-500 font-bold"
                                        title={text('msgNoSchedules', { connectorName: connector?.name })}
                                    />
                                )
                            }

                            return (
                                <span className="mb-1" key={connector.connectorId}>
                                    {getConnectorScheduleIcon()}
                                </span>
                            )
                        })}
                    </span>
                </div>
            </div>
        </Panel>
    )
}
